import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { appExpiryQuery, saveAppExpiry, type AppExpiryPage } from '../../api/appExpiry'
import {
  businessAppCopySourcesQuery,
  businessAppQuery,
  businessAppSupportSuggestionsQuery,
  copyBusinessAppSupport,
  saveBusinessAppSupport,
  type BusinessAppDetail,
  type SupportContact,
  type SupportSuggestions,
} from '../../api/businessApps'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { failed } from '../parts'
import { activeMarks, SuggestedMark, SuggestionsNote, withSuggestions } from './prefill'
import './SupportModal.css'

/**
 * "Support - <aplicatie>": tot suportul unei aplicatii intr-o singura
 * fereastra, deschisa din celula SUPPORT a listei si din fisa.
 *
 * Fara capete noi - scrie prin cele existente:
 *   POST /business-apps/{id}/support       furnizorul, contractul, portalul,
 *                                          programul, timpul de raspuns,
 *                                          ce le trebuie pe un caz, oamenii
 *   POST /business-apps/{id}/expiry        expirarea contractului - numai
 *                                          cand s-a schimbat; capatul scrie
 *                                          toate datele deodata, deci licenta
 *                                          si reinnoirea pleaca neschimbate
 *   POST /business-apps/{id}/copy-support  "Copy from another application"
 *
 * `/support` INLOCUIESTE blocul si oamenii, deci formularul porneste din fisa
 * proaspata si trimite inapoi tot, nu doar ce s-a atins.
 *
 * Precompletarea (6 octombrie 2026): campurile goale pornesc cu propunerile
 * din `GET /business-apps/{id}/support-suggestions` - furnizorul, portalul,
 * programul, timpul de raspuns, ce le trebuie pe un caz, contactele - cu
 * semnul "Suggested - from ..." si "Clear suggestions" (`prefill.tsx`). Nimic
 * nu se scrie pana la Save.
 */

export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

type ContactRow = { name: string; role: string; phone: string; email: string; level: number; levelNote: string }

const filled = (c: ContactRow) => c.name.trim() !== '' || c.email.trim() !== '' || c.phone.trim() !== ''

function sortedContacts(contacts: SupportContact[]): SupportContact[] {
  return [...contacts].sort((a, b) => (a.level === 0 ? 99 : a.level) - (b.level === 0 ? 99 : b.level))
}

export function SupportModal({
  appId,
  appName,
  onClose,
  onSaved,
}: {
  appId: number
  appName: string
  onClose(): void
  /** Dupa o scriere reusita; lipsa = doar se inchide. */
  onSaved?(): void
}) {
  const page = useQuery({ ...businessAppQuery(appId), refetchOnWindowFocus: false })
  const expiry = useQuery({ ...appExpiryQuery(appId), retry: false, refetchOnWindowFocus: false })
  const suggestions = useQuery(businessAppSupportSuggestionsQuery(appId))
  const title = `Support - ${appName}`

  /* Data de expirare e la alt capat: formularul asteapta si dupa el (reusit sau nu),
     ca sa nu porneasca cu o data goala peste una scrisa. */
  if (!page.data || (page.data as { error?: string }).error || expiry.isPending || suggestions.isPending) {
    return (
      <Modal id="supportModal" open large onClose={onClose} title={title}>
        {page.error ? (
          <div className="alert alert-danger">{failed(page.error)}</div>
        ) : (
          (page.data as { message?: string } | undefined)?.message ?? <Spinner />
        )}
      </Modal>
    )
  }

  return (
    <SupportForm
      page={page.data}
      expiry={expiry.data?.ready === true && expiry.data.canEdit !== false ? expiry.data : null}
      suggestions={suggestions.data}
      title={title}
      onClose={onClose}
      onSaved={onSaved ?? onClose}
    />
  )
}

function Group({ id, title, note, children }: { id: string; title: string; note: string; children: ReactNode }) {
  return (
    <fieldset className="app-sup-group" id={id}>
      <legend>{title}</legend>
      <p className="app-sup-note">{note}</p>
      {children}
    </fieldset>
  )
}

function SupportForm({
  page,
  expiry,
  suggestions,
  title,
  onClose,
  onSaved,
}: {
  page: BusinessAppDetail
  /** null = capatul de expirari nu e (inca) pe server sau omul nu-l poate scrie. */
  expiry: AppExpiryPage | null
  suggestions?: SupportSuggestions
  title: string
  onClose(): void
  onSaved(): void
}) {
  const query = useQueryClient()
  const app = page.app
  const s = page.support
  const contractEndBefore = expiry?.dates?.supportContractEnd ?? ''
  /* Fisa, cu propunerile peste campurile goale; contactele propuse numai
     cand aplicatia n-are niciun om. */
  const [prefill] = useState(() => withSuggestions(s, suggestions, { contacts: page.contacts.length === 0 }))
  const start = prefill.values
  const blankRow: ContactRow = { name: '', role: '', phone: '', email: '', level: 1, levelNote: '' }
  const [initialContacts] = useState<ContactRow[]>(() => {
    const rows = sortedContacts(page.contacts).map((c) => ({
      name: c.name,
      role: c.role,
      phone: c.phone,
      email: c.email,
      level: c.level,
      levelNote: c.levelNote,
    }))
    if (rows.length > 0) return rows
    /* Fara niciun om: cei propusi, altfel un rand gol de nivel 1, gata de scris. */
    return prefill.contacts.length > 0 ? prefill.contacts : [blankRow]
  })
  const [vendor, setVendor] = useState(start.vendor)
  const [portal, setPortal] = useState(start.portal)
  const [contract, setContract] = useState(s.contractNo)
  const [contractEnd, setContractEnd] = useState(contractEndBefore)
  const [days, setDays] = useState<number[]>(start.days)
  const [from, setFrom] = useState(start.from)
  const [to, setTo] = useState(start.to)
  const [tz, setTz] = useState(start.tz)
  const [hours, setHours] = useState(start.hours)
  const [needs, setNeeds] = useState(start.needs)
  const [contacts, setContacts] = useState<ContactRow[]>(initialContacts)
  const [cleared, setCleared] = useState(false)
  const marks = activeMarks(
    prefill.marks,
    start,
    { vendor, portal, days, from, to, tz, hours, needs },
    JSON.stringify(contacts) === JSON.stringify(initialContacts),
    cleared,
  )
  const suggesting = Object.keys(marks).length > 0

  /* "Clear suggestions": inapoi la ce e in fisa (si la programul implicit). */
  const clearSuggestions = () => {
    const saved = withSuggestions(s, null).values
    if (marks.vendor) setVendor(saved.vendor)
    if (marks.portal) setPortal(saved.portal)
    if (marks.schedule) {
      setDays(saved.days)
      setFrom(saved.from)
      setTo(saved.to)
      setTz(saved.tz)
    }
    if (marks.hours) setHours(saved.hours)
    if (marks.needs) setNeeds(saved.needs)
    if (marks.contacts) setContacts([blankRow])
    setCleared(true)
  }
  const [copyFrom, setCopyFrom] = useState(0)
  const [busy, setBusy] = useState(false)
  const sources = useQuery(businessAppCopySourcesQuery(app.id))

  const toggleDay = (d: number) => setDays((list) => (list.includes(d) ? list.filter((x) => x !== d) : [...list, d].sort()))
  const setContact = (i: number, patch: Partial<ContactRow>) => setContacts((list) => list.map((c, j) => (j === i ? { ...c, ...patch } : c)))

  const refresh = () => {
    void query.invalidateQueries({ queryKey: ['business-apps'] })
    void query.invalidateQueries({ queryKey: ['shell'] })
  }

  const write = async (): Promise<boolean> => {
    const saved = await saveBusinessAppSupport(app.id, {
      support_vendor: vendor,
      support_portal_url: portal,
      support_contract_no: contract,
      support_days: days,
      support_from: from,
      support_to: to,
      support_tz: tz,
      support_response_hours: hours,
      support_case_needs: needs,
      contacts: contacts.filter(filled),
    })
    if (saved.status !== 'OK') {
      toast(saved.message, 'Applications', 'error')
      return false
    }
    if (expiry !== null && contractEnd !== contractEndBefore) {
      const dates = expiry.dates
      const written = await saveAppExpiry(app.id, {
        support_contract_end: contractEnd,
        license_expires: dates?.licenseExpires ?? '',
        renewal_date: dates?.renewalDate ?? '',
        license_note: dates?.licenseNote ?? '',
      })
      if (written.status !== 'OK') {
        /* Blocul de suport e deja scris: lista se reimprospateaza oricum. */
        refresh()
        toast(written.message, 'Applications', 'error')
        return false
      }
    }
    return true
  }

  const save = () => {
    setBusy(true)
    write()
      .then((ok) => {
        if (!ok) return
        toast('Support details saved.', 'Applications')
        refresh()
        onSaved()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  const copy = () => {
    const source = sources.data?.sources.find((x) => x.id === copyFrom)
    if (!source) return
    if (!window.confirm(`Replace the support details of ${app.name} with those of ${source.name} (${source.companyName})?`)) return
    setBusy(true)
    copyBusinessAppSupport(app.id, source.id)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') {
          refresh()
          onSaved()
        }
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="supportModal"
      open
      large
      onClose={onClose}
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-default" id="supportCancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="supportSave" disabled={busy} onClick={save}>
            <i className="fas fa-check"></i> Save
          </button>
        </>
      }
    >
      {(sources.data?.sources?.length ?? 0) > 0 && (
        <div className="well well-sm form-inline app-sup-copy">
          <label htmlFor="supportCopy">Copy from another application</label>{' '}
          <select id="supportCopy" className="form-control input-sm" value={copyFrom} onChange={(e) => setCopyFrom(Number(e.target.value))}>
            <option value={0}>- choose -</option>
            {sources.data?.sources.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} - {x.companyName}
                {x.supportVendor !== '' ? ` (${x.supportVendor})` : ''}
              </option>
            ))}
          </select>{' '}
          <button type="button" className="btn btn-default btn-sm" id="supportCopyGo" disabled={copyFrom === 0 || busy} onClick={copy}>
            Copy
          </button>
        </div>
      )}

      {suggesting ? <SuggestionsNote id="supSuggested" onClear={clearSuggestions} /> : null}

      <Group
        id="supWho"
        title="Who supports it"
        note="The company that helps when the problem is in the software, not the server - we escalate to them."
      >
        <div className="row">
          <div className="col-sm-6 form-group">
            <label htmlFor="supVendor">Vendor / partner</label>
            <SuggestedMark field="vendor" label={marks.vendor} />
            <input id="supVendor" className="form-control" maxLength={190} placeholder="e.g. TotalSoft" value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </div>
          <div className="col-sm-6 form-group">
            <label htmlFor="supContract">Contract number</label>
            <input id="supContract" className="form-control" maxLength={100} placeholder="e.g. TS-2026-0417" value={contract} onChange={(e) => setContract(e.target.value)} />
          </div>
        </div>
        <div className="row">
          <div className={expiry !== null ? 'col-sm-8 form-group' : 'col-sm-12 form-group'}>
            <label htmlFor="supPortal">Their ticket portal</label>
            <SuggestedMark field="portal" label={marks.portal} />
            <input
              id="supPortal"
              className="form-control"
              maxLength={500}
              placeholder="e.g. https://support.totalsoft.ro"
              value={portal}
              onChange={(e) => setPortal(e.target.value)}
            />
          </div>
          {expiry !== null && (
            <div className="col-sm-4 form-group">
              <label htmlFor="supContractEnd">Contract ends</label>
              <input id="supContractEnd" type="date" className="form-control" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)} />
            </div>
          )}
        </div>
      </Group>

      <Group id="supWhen" title="Support hours and response time" note="When they answer and how fast - we use this to tell if they are late.">
        <div className="form-group">
          <SuggestedMark field="schedule" label={marks.schedule} />
          {DAY_NAMES.map((name, i) => (
            <label key={name} className="checkbox-inline">
              <input type="checkbox" checked={days.includes(i + 1)} onChange={() => toggleDay(i + 1)} /> {name}
            </label>
          ))}
        </div>
        <div className="row">
          <div className="col-sm-3 form-group">
            <label htmlFor="supFrom">From</label>
            <input id="supFrom" type="time" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="col-sm-3 form-group">
            <label htmlFor="supTo">To</label>
            <input id="supTo" type="time" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="col-sm-3 form-group">
            <label htmlFor="supTz">Time zone</label>
            <input id="supTz" className="form-control" placeholder="e.g. Europe/Bucharest" value={tz} onChange={(e) => setTz(e.target.value)} />
          </div>
          <div className="col-sm-3 form-group">
            <label htmlFor="supHours">Response time (hours)</label>
            <SuggestedMark field="hours" label={marks.hours} />
            <input id="supHours" type="number" min={1} className="form-control" placeholder="e.g. 4" value={hours} onChange={(e) => setHours(e.target.value)} />
          </div>
        </div>
      </Group>

      <Group
        id="supWhom"
        title="Escalation contacts"
        note="Who we call at the vendor, in order: level 1 first, then level 2 if they do not answer. Each one needs a phone or an email."
      >
        <SuggestedMark field="contacts" label={marks.contacts} />
        <table className="table table-condensed" id="supContacts">
          <thead>
            <tr>
              <th style={{ width: 120 }}>Level</th>
              <th>Name</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, i) => (
              <tr key={i}>
                <td>
                  <select className="form-control input-sm" aria-label="Level" value={c.level} onChange={(e) => setContact(i, { level: Number(e.target.value) })}>
                    <option value={0}>Not on path</option>
                    <option value={1}>Level 1</option>
                    <option value={2}>Level 2</option>
                    <option value={3}>Level 3</option>
                  </select>
                </td>
                <td>
                  <input className="form-control input-sm" aria-label="Name" placeholder="e.g. Helpdesk" value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} />
                </td>
                <td>
                  <input className="form-control input-sm" aria-label="Role" placeholder="e.g. account manager" value={c.role} onChange={(e) => setContact(i, { role: e.target.value })} />
                </td>
                <td>
                  <input className="form-control input-sm" aria-label="Phone" placeholder="e.g. +40 21 000 1111" value={c.phone} onChange={(e) => setContact(i, { phone: e.target.value })} />
                </td>
                <td>
                  <input
                    className="form-control input-sm"
                    aria-label="Email"
                    type="email"
                    placeholder="e.g. support@vendor.ro"
                    value={c.email}
                    onChange={(e) => setContact(i, { email: e.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="btn btn-default btn-xs" aria-label="Remove contact" onClick={() => setContacts((list) => list.filter((_, j) => j !== i))}>
                    <i className="fa fa-times"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          className="btn btn-default btn-sm"
          id="supContactAdd"
          onClick={() =>
            setContacts((list) => [...list, { name: '', role: '', phone: '', email: '', level: Math.min(3, list.filter((c) => c.level > 0).length + 1), levelNote: '' }])
          }
        >
          <i className="fas fa-plus"></i> Add person
        </button>
      </Group>

      <Group id="supNeedsGroup" title="What they need on a case" note="What we send with every new case, so they do not have to ask for it.">
        <SuggestedMark field="needs" label={marks.needs} />
        <textarea
          id="supNeeds"
          aria-label="What they need on a case"
          className="form-control"
          rows={3}
          placeholder="e.g. version, licence number, logs from C:\App\Logs, screenshots"
          value={needs}
          onChange={(e) => setNeeds(e.target.value)}
        />
      </Group>
    </Modal>
  )
}
